<template>
  <div class="home-cc home-dc">
    <section class="home-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ eyebrow }}</div>
        <h1 class="home-hero__title">
          {{ openTasks.length ? $tc('home.client.title', openTasks.length, { name: firstName, count: openTasks.length }) : $t('home.client.title_done', { name: firstName }) }}
        </h1>
        <p class="home-hero__summary">{{ $t('home.client.intro') }}</p>
        <div v-if="auditors.length" class="home-hero__chips">
          <span class="home-chip">
            <font-awesome-icon icon="users"/>{{ $t('home.client.auditors', { names: auditors.join(', ') }) }}
          </span>
        </div>
      </div>
      <div class="home-week home-week--center">
        <svg
            class="home-ring home-ring--large"
            width="110"
            height="110"
            viewBox="0 0 110 110"
        >
          <circle
              class="home-ring__track"
              cx="55"
              cy="55"
              r="48"
          />
          <circle
              v-if="deliveredCount"
              class="home-ring__value"
              cx="55"
              cy="55"
              r="48"
              :stroke-dasharray="ringDash"
          />
        </svg>
        <b class="home-week__count home-week__count--large">{{ deliveredCount }}/{{ totalCount }}</b>
        <div class="home-week__title">{{ $t('home.client.delivered') }}</div>
        <div v-if="nextDue" class="home-week__text">{{ deadlineText }}</div>
      </div>
    </section>

    <div class="v2-grid home-body home-dc__grid">
      <section class="v2-card">
        <div class="v2-card__head">
          <h3>{{ $t('home.client.tasks') }}</h3>
          <button v-if="projectGroups.length"
                  type="button"
                  class="home-btn home-btn--ghost home-btn--small"
                  qid="home-dc-toggle-all"
                  @click="toggleAll"
          >
            <font-awesome-icon :icon="anyExpanded ? 'compress' : 'expand'"/>
            {{ anyExpanded ? $t('home.work.collapse_all') : $t('home.work.expand_all') }}
          </button>
          <div class="v2-seg">
            <span :class="{ on: tab === 'todo' }" qid="home-dc-tab-todo" @click="tab = 'todo'">{{ $t('home.tabs.todo') }} <em class="home-count">{{ openTasks.length }}</em></span>
            <span :class="{ on: tab === 'returned' }" qid="home-dc-tab-returned" @click="tab = 'returned'">{{ $t('home.client.returned_tab') }} <em class="home-count" :class="{ 'is-red': returned.length }">{{ returned.length }}</em></span>
          </div>
        </div>

        <!-- Project, then control objective or element, then the tasks (as in My work), in the order of the project's task list -->
        <div v-for="group in projectGroups" :key="group.key" class="home-pgroup" qid="home-dc-project-group">
          <div class="home-group home-pgroup__head"
               role="button"
               tabindex="0"
               :aria-expanded="isCollapsed(group.key) ? 'false' : 'true'"
               @click="toggle(group.key)"
               @keydown.enter="toggle(group.key)"
          >
            <font-awesome-icon :icon="isCollapsed(group.key) ? 'angle-right' : 'angle-down'"/>
            <span class="home-pgroup__title">{{ group.project.title }}</span>
            <em class="home-count">{{ group.count }}</em>
            <span class="grow"></span>
            <span class="home-pgroup__due" :class="dueTone(group.due_date)">{{ dueText(group.due_date) }}</span>
          </div>
          <template v-if="!isCollapsed(group.key)">
            <client-row
                v-for="task in group.ungrouped"
                :key="'t-' + task.id"
                :task="task"
                :today="today"
                @open="$emit('open-task', task)"
                @reassign="reassign(task)"
            />
            <template v-for="sub in group.groups">
              <div :key="group.key + '-' + sub.key"
                   class="home-subgroup"
                   :class="{ 'is-collapsed': isCollapsed(subKey(group, sub)) }"
                   role="button"
                   tabindex="0"
                   :aria-expanded="isCollapsed(subKey(group, sub)) ? 'false' : 'true'"
                   qid="home-dc-task-subgroup"
                   @click="toggle(subKey(group, sub))"
                   @keydown.enter="toggle(subKey(group, sub))"
              >
                <font-awesome-icon :icon="isCollapsed(subKey(group, sub)) ? 'angle-right' : 'angle-down'" class="home-subgroup__chevron"/>
                <span class="home-subgroup__icon" :class="'is-' + sub.kind"><font-awesome-icon :icon="sub.icon"/></span>
                <span class="home-subgroup__title">{{ sub.title }}</span>
                <em class="home-count">{{ sub.tasks.length }}</em>
              </div>
              <template v-if="!isCollapsed(subKey(group, sub))">
                <client-row
                    v-for="task in sub.tasks"
                    :key="'t-' + task.id"
                    class="home-row--nested"
                    :task="task"
                    :today="today"
                    @open="$emit('open-task', task)"
                    @reassign="reassign(task)"
                />
              </template>
            </template>
          </template>
        </div>

        <div v-if="loading" class="home-empty"><div class="home-skeleton"></div><div class="home-skeleton home-skeleton--short"></div></div>
        <div v-else-if="(tab === 'todo' && !openTasks.length) || (tab === 'returned' && !returned.length)" class="home-empty">
          {{ $t('home.client.empty') }}
        </div>
        <div v-if="completedCount" class="home-more">
          <span>{{ $tc('home.client.delivered_count', completedCount, { count: completedCount }) }}</span>
        </div>
      </section>

      <div class="v2-col">
        <section v-if="evidence.total" class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('tasks.add_attachment.library') }}</h3><span class="v2-card__sub">{{ $tc('home.client.library_files', evidence.total, { count: evidence.total }) }}</span></div>
          <div class="v2-card__body home-muted">{{ $t('home.client.library_text') }}</div>
          <div v-for="file in evidence.files" :key="'f-' + file.id" class="home-file">
            <font-awesome-icon icon="file-alt"/>
            <span class="grow">{{ file.name }}</span>
            <span class="home-muted">{{ $tc('home.client.linked_tasks', file.tasks, { count: file.tasks }) }}</span>
          </div>
        </section>
        <section class="v2-card">
          <div class="v2-card__head"><h3>{{ $t('home.client.how_title') }}</h3></div>
          <div class="home-feed"><font-awesome-icon icon="eye"/><div>{{ $t('home.client.how_read') }}</div></div>
          <div class="home-feed"><font-awesome-icon icon="paperclip"/><div>{{ $t('home.client.how_add') }}</div></div>
          <div class="home-feed"><font-awesome-icon icon="check"/><div>{{ $t('home.client.how_deliver') }}</div></div>
        </section>

        <HomeGuides :guides="home.guides"/>
      </div>
    </div>

    <DataCollectionReassignModal ref="reassign" @saved="$emit('changed')"/>
  </div>
</template>

<script>
import { daysUntil, taskTitle, projectTree, dueTone, dueText } from '@/views/Home/homeTasks.js';
import DataCollectionReassignModal from '@/components/tasks/DataCollectionReassignModal.vue';
import HomeGuides from '@/views/Home/HomeGuides.vue';

const moment = require('moment');

const ClientRow = {
  name: 'HomeClientRow',
  props: { task: Object, today: String },
  computed: {
    status() {
      if (this.task.state === 'returned') {
        return { key: 'returned', color: 'red' };
      }
      if (this.task.test_phase === 'operating_effectiveness') {
        return { key: 'oe_sample', color: 'violet' };
      }
      return this.task.state === 'in_progress' ? { key: 'in_progress', color: 'blue' } : { key: 'not_started', color: 'grey' };
    },
    days() {
      return daysUntil(this.task.due_date, this.today);
    }
  },
  render(h) {
    const task = this.task;
    const signals = [];
    if (task.new_messages) {
      signals.push(h('span', { class: 'home-signal is-violet' }, [h('font-awesome-icon', { props: { icon: 'comment' } }), ' ' + task.new_messages]));
    }
    if (task.files) {
      signals.push(h('span', { class: 'home-signal' }, [h('font-awesome-icon', { props: { icon: 'paperclip' } }), ' ' + task.files]));
    }
    const dueClass = this.days !== null && this.days < 0 ? 'is-red' : (this.days === 0 ? 'is-amber' : '');
    return h('div', {
      class: 'home-row home-row--client',
      attrs: { role: 'button', tabindex: 0, qid: 'home-dc-task' },
      on: { click: () => this.$emit('open'), keydown: event => { if (event.key === 'Enter') this.$emit('open'); } }
    }, [
      h('div', { class: 'home-row__main' }, [
        h('div', { class: 'home-row__title' }, taskTitle(this, task)),
        h('div', { class: 'home-row__context' }, task.request || task.project.title)
      ]),
      ...signals,
      h('span', { class: 'home-tag is-' + this.status.color }, this.$t('home.client.status.' + this.status.key)),
      h('span', { class: 'home-row__due ' + dueClass }, task.due_date ? moment(task.due_date, 'YYYY-MM-DD').format('D MMM') : '–'),
      // Documentation collection tasks can be handed to someone else (or a new user, invited from the dialog)
      task.kind === 'data' ? h('button', {
        class: 'home-btn home-btn--ghost home-btn--small',
        attrs: { type: 'button', qid: 'home-dc-reassign', title: this.$t('tasks.re_assign.data_collection_title') },
        on: { click: event => { event.stopPropagation(); this.$emit('reassign'); } }
      }, [h('font-awesome-icon', { props: { icon: 'user-tag' } }), ' ' + this.$t('tasks.re_assign_button')]) : null,
      h('button', { class: 'home-btn home-btn--small', attrs: { type: 'button' }, on: { click: event => { event.stopPropagation(); this.$emit('open'); } } }, this.$t('home.actions.open'))
    ]);
  }
};

export default {
  name: 'HomeDocumentationCollection',
  components: { ClientRow, DataCollectionReassignModal, HomeGuides },
  props: {
    home: { type: Object, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      tab: 'todo',
      collapsed: []
    };
  },
  computed: {
    getClient() {
      return this.$store.state.client;
    },
    user() {
      return this.$store.state.user || {};
    },
    firstName() {
      return (this.user.name || '').split(' ')[0];
    },
    today() {
      return this.home.today || moment().format('YYYY-MM-DD');
    },
    // Also the management statement and system description sent as a task (US 9394)
    openTasks() {
      return (this.home.tasks || []).filter(task => ['data', 'main', 'statement'].includes(task.kind));
    },
    returned() {
      return this.openTasks.filter(task => task.state === 'returned');
    },
    others() {
      return this.openTasks.filter(task => task.state !== 'returned');
    },
    // The tab's tasks by project and control objective or element, in the order of the project's task list
    projectGroups() {
      return projectTree(this.tab === 'returned' ? this.returned : this.openTasks);
    },
    groupKeys() {
      return [].concat(...this.projectGroups.map(group => [group.key].concat(group.groups.map(sub => this.subKey(group, sub)))));
    },
    anyExpanded() {
      return this.groupKeys.some(key => !this.collapsed.includes(key));
    },
    completedCount() {
      return this.home.completed_count || 0;
    },
    deliveredCount() {
      return this.completedCount;
    },
    totalCount() {
      return this.completedCount + this.openTasks.length;
    },
    ringDash() {
      const length = 2 * Math.PI * 48;
      const share = this.totalCount ? this.deliveredCount / this.totalCount : 0;
      return (length * share) + ' ' + length;
    },
    projects() {
      const seen = {};
      return this.openTasks.map(task => task.project).filter(project => {
        if (seen[project.id]) {
          return false;
        }
        seen[project.id] = true;
        return true;
      });
    },
    eyebrow() {
      const name = this.getClient ? this.getClient.name : '';
      return [name].concat(this.projects.slice(0, 2).map(project => project.title)).filter(Boolean).join(' · ');
    },
    auditors() {
      return this.home.auditors || [];
    },
    nextDue() {
      const dates = this.openTasks.map(task => task.due_date).filter(Boolean).sort();
      return dates.length ? dates[0] : null;
    },
    daysLeft() {
      return daysUntil(this.nextDue, this.today);
    },
    deadlineText() {
      const date = this.formatDay(this.nextDue);
      return this.daysLeft < 0 ? this.$t('home.client.deadline_passed', { date }) : this.$t('home.client.deadline', { date, days: this.daysLeft });
    },
    evidence() {
      return this.home.evidence || { total: 0, files: [] };
    }
  },
  methods: {
    subKey(group, sub) {
      return group.key + '/' + sub.key;
    },
    isCollapsed(key) {
      return this.collapsed.includes(key);
    },
    toggle(key) {
      this.collapsed = this.isCollapsed(key)
        ? this.collapsed.filter(item => item !== key)
        : this.collapsed.concat([key]);
    },
    toggleAll() {
      this.collapsed = this.anyExpanded ? this.groupKeys.slice() : [];
    },
    dueTone(date) {
      return dueTone(date, this.today);
    },
    dueText(date) {
      return dueText(this, date, this.today);
    },
    reassign(task) {
      this.$refs.reassign.show(task);
    },
    formatDay(date) {
      return moment(date, 'YYYY-MM-DD').format('dddd D MMMM');
    }
  }
};
</script>
